import { validate } from 'ts-schema';

/**
 * Replace invalid characters with an underscore.
 * https://spec.graphql.org/June2018/#sec-Names
 */
export function toValidGraphQLName(path, method = 'get') {
  // Replace the first character if it's not a letter or underscore
  // Replace the rest of the characters if they're not alphanumeric or underscore
  return `${method}-${path}`.replace(/^[^_A-Za-z]/, '_').replace(/[^_0-9A-Za-z]/g, '_');
}

// @TODO Replace with enviro
export function isProd() {
  return process.env.NODE_ENV === 'production';
}

/**
 * Replace path parameters with values.
 * @example
 * injectPathWithValues('/app-users/v1/users/{userId}/sync-user-object', { userId: 123 })
 * // => '/app-users/v1/users/123/sync-user-object'
 */
export function injectPathWithValues(path, values) {
  return path.replace(/{(\w+)}/g, (_, key) => {
    const value = values[key];
    if (value === undefined) {
      throw new Error(`Missing path parameter: ${key}`);
    }
    return String(value);
  });
}
export function validateError(schema, error) {
  if (!isProd() && !validate(schema, error)) {
    console.warn('Runtime error does not match type (this could be due to a non-network error).', {
      error,
      schema
    });
  }
}
export function validateResponse(schema, response) {
  if (!isProd() && !validate(schema, response)) {
    console.error('🚨 Runtime response does not match type definition!', {
      response,
      schema
    });
  }
}
export function validateRequest(schema, request) {
  if (!isProd() && !validate(schema, request)) {
    console.error('🚨 Runtime request does not match type definition!', {
      request,
      schema
    });
  }
}
export class BatchFetcher {
  constructor(fetcher, batchSize = 10) {
    this.requestId = null;
    this.state = new Map();
    this.batchSize = 10;
    this.fetcher = fetcher;
    this.batchSize = batchSize;
  }
  clearCache(ids) {
    ids.forEach(id => {
      var _this$state$get;
      if (((_this$state$get = this.state.get(id)) === null || _this$state$get === void 0 ? void 0 : _this$state$get.status) === 'pending') {
        // Don't want to delete a pending request or it will hang
        return;
      }
      this.state.delete(id);
    });
  }
  fetch(ids, useCache = true) {
    if (!useCache) {
      this.clearCache(ids);
    }
    const promises = ids.map(id => {
      var _this$state$get2, _this$state$get3;
      if (((_this$state$get2 = this.state.get(id)) === null || _this$state$get2 === void 0 ? void 0 : _this$state$get2.status) === 'resolved' || ((_this$state$get3 = this.state.get(id)) === null || _this$state$get3 === void 0 ? void 0 : _this$state$get3.status) === 'pending') {
        return this.state.get(id).promise;
      }
      let onReject;
      let onResolve;
      const promise = new Promise((resolve, reject) => {
        onReject = reject;
        onResolve = resolve;
      });
      this.state.set(id, {
        promise,
        onReject: onReject,
        onResolve: onResolve,
        status: 'pending'
      });
      return promise;
    });
    if (!this.requestId) {
      this.requestId = setTimeout(() => this.executeBatchFetch(), 0);
    }
    return Promise.all(promises);
  }
  getIdsToFetch() {
    return [...this.state.keys()].filter(id => {
      var _this$state$get4;
      return ((_this$state$get4 = this.state.get(id)) === null || _this$state$get4 === void 0 ? void 0 : _this$state$get4.status) === 'pending';
    }).slice(0, this.batchSize);
  }
  executeBatchFetch() {
    const idsToFetch = this.getIdsToFetch();
    return this.fetcher(idsToFetch).then(responses => {
      idsToFetch.forEach((id, i) => {
        this.state.get(id).onResolve(responses[i]);
        this.state.get(id).status = 'resolved';
      });
    }).catch(error => {
      idsToFetch.forEach(id => {
        var _this$state$get5;
        (_this$state$get5 = this.state.get(id)) === null || _this$state$get5 === void 0 || _this$state$get5.onReject(error);
        this.state.get(id).status = 'rejected';
      });
    }).finally(() => {
      this.requestId = null;
      if (this.getIdsToFetch().length > 0) {
        this.requestId = setTimeout(() => this.executeBatchFetch(), 0);
      }
    });
  }
}